import { z } from 'zod';
import { ACTION_ITEM_TYPE, type ActionItemType } from '@trulyinbox/types';

/**
 * The "Sending limits" tab. A web-only filter value: limit alerts are org-level
 * and come back on every response, so the server has no `type` for them and
 * must never be sent this one — `toServerType` drops it.
 */
export const LIMITS_TAB = 'LIMITS' as const;

export type ActionItemsTab = ActionItemType | typeof LIMITS_TAB;

/**
 * URL search-parameter schema for `/action-items`. The URL is the single source
 * of truth for the tab and the page, so a refresh, a deep link and the back
 * button all reproduce the same view.
 *
 * Both degrade rather than throw: an unknown `type` reads as "all", and a bad
 * `page` reads as 1 — a hand-edited link should land on a page, not an error.
 */
export const actionItemsSearchSchema = z.object({
  type: z
    .union([z.nativeEnum(ACTION_ITEM_TYPE), z.literal(LIMITS_TAB)])
    .optional()
    .catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

export type ActionItemsSearch = z.infer<typeof actionItemsSearchSchema>;

/** The `type` the API understands — the Sending limits tab fetches unfiltered. */
export function toServerType(tab: ActionItemsTab | undefined): ActionItemType | undefined {
  return tab === LIMITS_TAB ? undefined : tab;
}
